/**
 * Shared weapon basic-attack contact geometry authority — the ratified
 * PR29/BALANCE.0A spatial/contact resolver, in a production-neutral module so
 * both the Balance Lab and the `AuthoritativeWorld` weapon basic attack
 * consume the exact same geometry.
 * Geometry and contact semantics ONLY. Damage and cadence do not live here.
 */
import type { BalanceWeaponId } from '../reference/weapon-progression-v0.js';

export interface BalanceVec2 { x: number; y: number; }
export interface BalanceEnemyState extends BalanceVec2 {
  id: number;
  hp: number;
  maxHp: number;
  active: boolean;
  resolution: 'ACTIVE' | 'KILLED' | 'REACHED';
  /** Each enemy's actual radius overrides the shared fallback. */
  radiusM?: number;
}
export interface PhysicalWeaponShape {
  weaponLength: number;
  primaryDamageLength: number;
  primaryWidth: number;
  weaponCount: 1 | 2;
}
export type StandardBalanceWeaponId = Exclude<BalanceWeaponId, 'ASSASSIN_BLADE' | 'GREATSWORD'>;
export type BalanceSpatialProfile =
  | { readonly id: 'DAGGER_PAIR'; readonly shape: PhysicalWeaponShape; readonly effectiveReachM: number; readonly leftSweepDeg: 140; readonly rightSweepDeg: 140; readonly overlapDeg: 40; readonly damagePerHandFraction: 0.5 }
  | { readonly id: 'SWORD'; readonly shape: PhysicalWeaponShape; readonly effectiveReachM: number; readonly sweepDeg: 125 }
  | { readonly id: 'HEAVY_AXE'; readonly shape: PhysicalWeaponShape; readonly effectiveReachM: number; readonly sweepDeg: 120; readonly shaftDamageFraction: 0.35 }
  | { readonly id: 'SPEAR'; readonly shape: PhysicalWeaponShape; readonly effectiveReachM: number; readonly acquisitionDeg: 80; readonly minThrustDistanceM: 1.5; readonly closeSweepDeg: 120; readonly shaftDamageFraction: 0.25 }
  | { readonly id: 'BOW_REFERENCE'; readonly maxTargetsPerActivation: 1 };

export const BALANCE_HAND_OFFSET_M = 0.55;
/** Melee-only center-reach adjustment; physical weapon dimensions and Bow range are unchanged. */
export const MELEE_REACH_EXTENSION_M = 0.2;

/** Geometry alone — attack speed and damage modifiers belong to basic-attack.ts. */
export const BALANCE_WEAPON_GEOMETRY_V0 = Object.freeze({
  DAGGER_PAIR: Object.freeze({
    id: 'DAGGER_PAIR',
    shape: Object.freeze({ weaponLength: 0.6, primaryDamageLength: 0.6, primaryWidth: 0.1, weaponCount: 2 as const }),
    effectiveReachM: 1.30 + MELEE_REACH_EXTENSION_M,
    leftSweepDeg: 140 as const,
    rightSweepDeg: 140 as const,
    overlapDeg: 40 as const,
    damagePerHandFraction: 0.5 as const,
  }),
  SWORD: Object.freeze({
    id: 'SWORD',
    shape: Object.freeze({ weaponLength: 1.0, primaryDamageLength: 1.0, primaryWidth: 0.16, weaponCount: 1 as const }),
    effectiveReachM: 2.2,
    sweepDeg: 125 as const,
  }),
  HEAVY_AXE: Object.freeze({
    id: 'HEAVY_AXE',
    shape: Object.freeze({ weaponLength: 2.0, primaryDamageLength: 0.7, primaryWidth: 0.8, weaponCount: 1 as const }),
    effectiveReachM: BALANCE_HAND_OFFSET_M + 2.0 + MELEE_REACH_EXTENSION_M,
    sweepDeg: 120 as const,
    shaftDamageFraction: 0.35 as const,
  }),
  SPEAR: Object.freeze({
    id: 'SPEAR',
    shape: Object.freeze({ weaponLength: 3.0, primaryDamageLength: 0.5, primaryWidth: 0.1, weaponCount: 1 as const }),
    effectiveReachM: BALANCE_HAND_OFFSET_M + 3.0 + MELEE_REACH_EXTENSION_M,
    acquisitionDeg: 80 as const,
    minThrustDistanceM: 1.5,
    closeSweepDeg: 120 as const,
    shaftDamageFraction: 0.25 as const,
  }),
  BOW_REFERENCE: Object.freeze({ id: 'BOW_REFERENCE', maxTargetsPerActivation: 1 as const }),
} satisfies Record<StandardBalanceWeaponId, BalanceSpatialProfile>);

export const BALANCE_BOW_REFERENCE_RANGE_M = 12;
export const BALANCE_BOW_ACQUISITION_DEG = 80;

/** Greatsword and assassin blade have no target cap; area itself determines hits. */
export const BALANCE_SPECIAL_GEOMETRY_V0 = Object.freeze({
  ASSASSIN_BLADE: Object.freeze({ reachM: 2.0, sectorDeg: 240 }),
  GREATSWORD: Object.freeze({ reachM: 2.8, sectorDeg: 135 }),
});

/**
 * The ids a weapon-contact skill may borrow a shape from: every balance weapon
 * plus `CATALYST`, the one contact profile the three catalyst weapons share.
 * A catalyst has no basic melee attack, so it is deliberately not a
 * `BalanceWeaponId` (that union is the set the basic-attack resolver owns).
 */
export type WeaponContactProfileId = BalanceWeaponId | 'CATALYST';

/**
 * **PROVISIONAL (owner decision 2026-10-18).** A catalyst held as a weapon is
 * sword-sized: the same blade length and width, the same reach and sweep as
 * `SWORD`. It is a contact shape only; the catalyst's damage and cadence are
 * still its own source's.
 */
export const BALANCE_CATALYST_CONTACT_V0 = Object.freeze({
  id: 'CATALYST' as const,
  shape: Object.freeze({ weaponLength: 1.0, primaryDamageLength: 1.0, primaryWidth: 0.16, weaponCount: 1 as const }),
  effectiveReachM: 2.2,
  sweepDeg: 125 as const,
});

/**
 * A weapon's crafted-thrust corridor: a straight strip of `widthM` running
 * `reachM` from the wielder. Only a skill whose motion is `THRUST` (or, for the
 * profile that says so, the legacy INSTANT spelling) uses it; a basic attack
 * never does.
 *
 * One rule builds every corridor but the spear's (owner decision 2026-10-18): a
 * thrust is the weapon's own hitbox stretched along the aim — length
 * `max(the weapon's reach, BALANCE_THRUST_MIN_REACH_M)`, width the weapon's
 * blade width (`shape.primaryWidth`), the whole of it blade. A weapon with no
 * corridor (the axe) is simply absent. SPEAR's corridor is its own ratified
 * far-thrust geometry.
 *
 * **PROVISIONAL**: a weapon with no `shape` entry has no measured blade width —
 * GREATSWORD's `BALANCE_GREATSWORD_THRUST_WIDTH_M` and ASSASSIN_BLADE's
 * `BALANCE_ASSASSIN_BLADE_THRUST_WIDTH_M` are chosen, not measured. The dagger
 * pair thrusts as one corridor, the whole blade at full hit (the sweep's
 * per-hand half hits do not apply).
 */
export interface BalanceThrustProfile {
  readonly reachM: number;
  readonly widthM: number;
  /** The whole corridor is blade: no shaft region, no close sweep. */
  readonly wholeBlade?: true;
  /** Stored skills spell this thrust as `INSTANT`; it keeps loading and acting as a thrust. */
  readonly legacyInstantMotion?: true;
}
/** Shortest corridor any thrust runs (owner decision 2026-10-18). */
export const BALANCE_THRUST_MIN_REACH_M = 3.0;
export const BALANCE_GREATSWORD_THRUST_WIDTH_M = 0.25;
/** PROVISIONAL: the assassin blade has no shape entry; it takes its LIGHT sibling's blade width. */
export const BALANCE_ASSASSIN_BLADE_THRUST_WIDTH_M = BALANCE_WEAPON_GEOMETRY_V0.DAGGER_PAIR.shape.primaryWidth;

function wholeBladeCorridor(reachM: number, widthM: number): BalanceThrustProfile {
  return Object.freeze({ reachM: Math.max(reachM, BALANCE_THRUST_MIN_REACH_M), widthM, wholeBlade: true as const });
}

export const BALANCE_THRUST_GEOMETRY_V0: Readonly<Partial<Record<WeaponContactProfileId, BalanceThrustProfile>>> = Object.freeze({
  SPEAR: Object.freeze({
    reachM: BALANCE_WEAPON_GEOMETRY_V0.SPEAR.effectiveReachM,
    widthM: BALANCE_WEAPON_GEOMETRY_V0.SPEAR.shape.primaryWidth,
    legacyInstantMotion: true as const,
  }),
  DAGGER_PAIR: wholeBladeCorridor(BALANCE_WEAPON_GEOMETRY_V0.DAGGER_PAIR.effectiveReachM, BALANCE_WEAPON_GEOMETRY_V0.DAGGER_PAIR.shape.primaryWidth),
  SWORD: wholeBladeCorridor(BALANCE_WEAPON_GEOMETRY_V0.SWORD.effectiveReachM, BALANCE_WEAPON_GEOMETRY_V0.SWORD.shape.primaryWidth),
  GREATSWORD: wholeBladeCorridor(BALANCE_SPECIAL_GEOMETRY_V0.GREATSWORD.reachM, BALANCE_GREATSWORD_THRUST_WIDTH_M),
  ASSASSIN_BLADE: wholeBladeCorridor(BALANCE_SPECIAL_GEOMETRY_V0.ASSASSIN_BLADE.reachM, BALANCE_ASSASSIN_BLADE_THRUST_WIDTH_M),
  CATALYST: wholeBladeCorridor(BALANCE_CATALYST_CONTACT_V0.effectiveReachM, BALANCE_CATALYST_CONTACT_V0.shape.primaryWidth),
});

/**
 * Corridors this table held and no longer does. A saved skill names only its
 * weapon and its motion, never the length it was priced at, so the table keeps
 * what a corridor used to be: a stored skill on an entry here may have been
 * priced for the old one (`saved-skill-reprice.ts`). Add an entry when a
 * corridor changes; never edit one.
 */
export const BALANCE_THRUST_GEOMETRY_SUPERSEDED: readonly { readonly profileId: WeaponContactProfileId; readonly reachM: number; readonly widthM: number }[] = Object.freeze([
  // 2026-10-18: every corridor but the spear's runs at least BALANCE_THRUST_MIN_REACH_M.
  Object.freeze({ profileId: 'SWORD' as const, reachM: 2.2, widthM: 0.16 }),
  Object.freeze({ profileId: 'GREATSWORD' as const, reachM: 2.8, widthM: 0.25 }),
]);

export interface BalanceAttackResolution {
  mode: string;
  damageByEnemyId: ReadonlyMap<number, number>;
}
function length(v: BalanceVec2): number { return Math.hypot(v.x, v.y); }
function effectiveRadiusM(enemy: BalanceEnemyState, fallbackM: number): number { return enemy.radiusM ?? fallbackM; }
function relativeToPlayer(enemy: BalanceEnemyState, player: BalanceVec2): BalanceVec2 { return { x: enemy.x - player.x, y: enemy.y - player.y }; }
function angleDegFromForward(relative: BalanceVec2): number { return (Math.atan2(relative.y, relative.x) * 180) / Math.PI; }
function activeEnemies(enemies: readonly BalanceEnemyState[]): BalanceEnemyState[] { return enemies.filter((enemy) => enemy.active); }
function addDamage(map: Map<number, number>, enemyId: number, damage: number): void { map.set(enemyId, (map.get(enemyId) ?? 0) + damage); }
function isInForwardSector(relative: BalanceVec2, rangeM: number, sectorDeg: number, enemyRadiusM: number): boolean {
  if (length(relative) > rangeM + enemyRadiusM) return false;
  return Math.abs(angleDegFromForward(relative)) <= sectorDeg / 2;
}
function isInAngleBand(relative: BalanceVec2, rangeM: number, minDeg: number, maxDeg: number, enemyRadiusM: number): boolean {
  if (length(relative) > rangeM + enemyRadiusM) return false;
  const angle = angleDegFromForward(relative);
  return angle >= minDeg && angle <= maxDeg;
}
function pointToDirectedSegmentContact(
  relative: BalanceVec2,
  direction: BalanceVec2,
  segmentLengthM: number,
  halfWidthM: number,
  enemyRadiusM: number,
): { projectionM: number; lateralM: number } | null {
  const projectionM = relative.x * direction.x + relative.y * direction.y;
  if (projectionM < 0 || projectionM > segmentLengthM + enemyRadiusM) return null;
  const lateralM = Math.abs(relative.x * direction.y - relative.y * direction.x);
  if (lateralM > halfWidthM + enemyRadiusM) return null;
  return { projectionM, lateralM };
}
function withinSector(enemy: BalanceEnemyState, reachM: number, sectorDeg: number, enemyRadiusM: number): boolean {
  if (!enemy.active || Math.hypot(enemy.x, enemy.y) > reachM + enemyRadiusM) return false;
  return Math.abs((Math.atan2(enemy.y, enemy.x) * 180) / Math.PI) <= sectorDeg / 2;
}

/** Each hand of the dagger gets a 140-degree sweep, with a 40-degree overlap.
 * The exact union is 240 degrees; overlap contacts add two half-damage hits. */
export function daggerSweepAngleBands(): Readonly<{ left: readonly [number, number]; right: readonly [number, number] }> {
  const { leftSweepDeg, rightSweepDeg, overlapDeg } = BALANCE_WEAPON_GEOMETRY_V0.DAGGER_PAIR;
  const halfOverlap = overlapDeg / 2;
  return { left: [-leftSweepDeg + halfOverlap, halfOverlap], right: [-halfOverlap, rightSweepDeg - halfOverlap] };
}

/** Resolve one current balance basic-attack opportunity. */
export function resolveBalanceAttack(
  weaponId: BalanceWeaponId,
  enemies: readonly BalanceEnemyState[],
  hitDamage: number,
  enemyRadiusM: number,
  weaponReachOverrideM?: number,
): BalanceAttackResolution {
  const damage = new Map<number, number>();
  const alive = activeEnemies(enemies);
  const player = { x: 0, y: 0 };
  if (weaponId === 'ASSASSIN_BLADE' || weaponId === 'GREATSWORD') {
    const p = BALANCE_SPECIAL_GEOMETRY_V0[weaponId];
    const reach = weaponReachOverrideM ?? p.reachM;
    const candidates = enemies
      .filter((enemy) => withinSector(enemy, reach, p.sectorDeg, effectiveRadiusM(enemy, enemyRadiusM)))
      .sort((a, b) => Math.hypot(a.x, a.y) - Math.hypot(b.x, b.y) || a.id - b.id);
    return { mode: weaponId === 'ASSASSIN_BLADE' ? 'ASSASSIN_AREA_SLASH' : 'GREATSWORD_SECTOR', damageByEnemyId: new Map(candidates.map((enemy) => [enemy.id, hitDamage])) };
  }
  const weapon = BALANCE_WEAPON_GEOMETRY_V0[weaponId];
  switch (weapon.id) {
    case 'DAGGER_PAIR': {
      const reach = weaponReachOverrideM ?? weapon.effectiveReachM;
      const bands = daggerSweepAngleBands();
      for (const enemy of alive) {
        const relative = relativeToPlayer(enemy, player);
        const radius = effectiveRadiusM(enemy, enemyRadiusM);
        if (isInAngleBand(relative, reach, bands.left[0], bands.left[1], radius)) addDamage(damage, enemy.id, hitDamage * weapon.damagePerHandFraction);
        if (isInAngleBand(relative, reach, bands.right[0], bands.right[1], radius)) addDamage(damage, enemy.id, hitDamage * weapon.damagePerHandFraction);
      }
      return { mode: 'DAGGER_DUAL_SWEEP', damageByEnemyId: damage };
    }
    case 'SWORD': {
      for (const enemy of alive) {
        const relative = relativeToPlayer(enemy, player);
        if (isInForwardSector(relative, weapon.effectiveReachM, weapon.sweepDeg, effectiveRadiusM(enemy, enemyRadiusM))) addDamage(damage, enemy.id, hitDamage);
      }
      return { mode: 'SWORD_SWEEP', damageByEnemyId: damage };
    }
    case 'HEAVY_AXE': {
      const headStartM = weapon.effectiveReachM - weapon.shape.primaryDamageLength;
      for (const enemy of alive) {
        const relative = relativeToPlayer(enemy, player);
        const radius = effectiveRadiusM(enemy, enemyRadiusM);
        if (!isInForwardSector(relative, weapon.effectiveReachM, weapon.sweepDeg, radius)) continue;
        const hitsHead = length(relative) + radius >= headStartM;
        addDamage(damage, enemy.id, hitDamage * (hitsHead ? 1 : weapon.shaftDamageFraction));
      }
      return { mode: 'AXE_SWEEP', damageByEnemyId: damage };
    }
    case 'SPEAR': {
      const candidates = alive
        .map((enemy) => ({ enemy, relative: relativeToPlayer(enemy, player) }))
        .filter(({ enemy, relative }) => isInForwardSector(relative, weapon.effectiveReachM, weapon.acquisitionDeg, effectiveRadiusM(enemy, enemyRadiusM)))
        .sort((a, b) => length(a.relative) - length(b.relative) || a.enemy.id - b.enemy.id);
      const target = candidates[0];
      if (target === undefined) return { mode: 'NO_TARGET', damageByEnemyId: damage };
      const targetDistanceM = length(target.relative);
      if (targetDistanceM <= weapon.minThrustDistanceM) {
        const tipStartM = weapon.effectiveReachM - weapon.shape.primaryDamageLength;
        for (const enemy of alive) {
          const relative = relativeToPlayer(enemy, player);
          const radius = effectiveRadiusM(enemy, enemyRadiusM);
          if (!isInForwardSector(relative, weapon.effectiveReachM, weapon.closeSweepDeg, radius)) continue;
          const hitsTip = length(relative) + radius >= tipStartM;
          addDamage(damage, enemy.id, hitDamage * (hitsTip ? 1 : weapon.shaftDamageFraction));
        }
        return { mode: 'SPEAR_CLOSE_SWEEP', damageByEnemyId: damage };
      }
      const direction = { x: target.relative.x / targetDistanceM, y: target.relative.y / targetDistanceM };
      const extensionM = weapon.effectiveReachM;
      const tipStartM = Math.max(0, Math.min(targetDistanceM, weapon.effectiveReachM) - weapon.shape.primaryDamageLength);
      for (const enemy of alive) {
        const relative = relativeToPlayer(enemy, player);
        const radius = effectiveRadiusM(enemy, enemyRadiusM);
        const contact = pointToDirectedSegmentContact(relative, direction, extensionM, weapon.shape.primaryWidth / 2, radius);
        if (contact === null) continue;
        const hitsTip = contact.projectionM + radius >= tipStartM;
        addDamage(damage, enemy.id, hitDamage * (hitsTip ? 1 : weapon.shaftDamageFraction));
      }
      return { mode: 'SPEAR_THRUST', damageByEnemyId: damage };
    }
    case 'BOW_REFERENCE': {
      const candidates = alive
        .map((enemy) => ({ enemy, relative: relativeToPlayer(enemy, player) }))
        .filter(({ enemy, relative }) => isInForwardSector(relative, BALANCE_BOW_REFERENCE_RANGE_M, BALANCE_BOW_ACQUISITION_DEG, effectiveRadiusM(enemy, enemyRadiusM)))
        .sort((a, b) => length(a.relative) - length(b.relative) || a.enemy.id - b.enemy.id);
      const target = candidates[0];
      if (target === undefined) return { mode: 'NO_TARGET', damageByEnemyId: damage };
      addDamage(damage, target.enemy.id, hitDamage);
      return { mode: 'BOW_SINGLE', damageByEnemyId: damage };
    }
  }
}
